import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Twitter, Linkedin, Instagram, Youtube, Github, Facebook, AtSign, Send, MessageCircle, Cloud, Music, Mail, ArrowUpRight } from "lucide-react";
import Newsletter from "@/components/site/Newsletter";
import { Marquee } from "@/components/motion/primitives";
import { getSettings } from "@/lib/api";

const COLS = [
  { title: "Work With Me", links: [["Pricing & Plans", "/pricing"], ["Start My Project", "/start-project"], ["Build My Website", "/build"], ["Free Website Audit", "/free-website-audit"], ["Website Care Plans", "/pricing"]] },
  { title: "Services", links: [["Web Design & Development", "/services/web-design"], ["SEO & Visibility", "/services/seo-marketing"], ["Website Care", "/services/website-care"], ["Digital Growth", "/services/digital-growth"], ["All Services", "/services"]] },
  { title: "Markets", links: [["Web Design in the US", "/web-design/united-states"], ["Web Design in the UK", "/web-design/united-kingdom"], ["Web Design in Ireland", "/web-design/ireland"], ["Web Design in South Africa", "/web-design/south-africa"], ["All Markets", "/web-design"]] },
  { title: "Explore", links: [["Free Tools", "/tools"], ["The Playbook (Free PDF)", "/free-playbook"], ["Blog", "/blog"], ["Shop", "/shop"], ["Clients", "/clients"]] },
  { title: "Company", links: [["Programs", "/clients"], ["Projects", "/projects"], ["About", "/about"], ["Contact", "/contact"]] },
];

// Every social network the admin can save, in the order they appear in the footer.
const SOCIAL_NETWORKS = [
  { key: "twitter",   Icon: Twitter,       label: "X / Twitter",  fallback: "https://twitter.com/juliusmason" },
  { key: "linkedin",  Icon: Linkedin,      label: "LinkedIn",     fallback: "https://linkedin.com/in/juliusmason" },
  { key: "instagram", Icon: Instagram,     label: "Instagram",    fallback: "https://instagram.com/juliusmason" },
  { key: "facebook",  Icon: Facebook,      label: "Facebook",     fallback: "" },
  { key: "youtube",   Icon: Youtube,       label: "YouTube",      fallback: "https://youtube.com/@juliusmason" },
  { key: "tiktok",    Icon: Music,         label: "TikTok",       fallback: "" },
  { key: "threads",   Icon: AtSign,        label: "Threads",      fallback: "" },
  { key: "pinterest", Icon: ArrowUpRight,  label: "Pinterest",    fallback: "" },
  { key: "whatsapp",  Icon: MessageCircle, label: "WhatsApp",     fallback: "" },
  { key: "telegram",  Icon: Send,          label: "Telegram",     fallback: "" },
  { key: "bluesky",   Icon: Cloud,         label: "Bluesky",      fallback: "" },
  { key: "github",    Icon: Github,        label: "GitHub",       fallback: "https://github.com/juliusmason" },
];

export default function Footer() {
  const [social, setSocial] = useState({});
  useEffect(() => {
    getSettings().then((s) => setSocial(s?.social || {})).catch(() => setSocial({}));
  }, []);

  const activeNetworks = SOCIAL_NETWORKS
    .map((n) => ({ ...n, url: (social[n.key] || n.fallback || "").trim() }))
    .filter((n) => n.url);

  return (
    <footer data-testid="site-footer" className="bg-background border-t border-border">
      <Marquee className="border-b border-border py-6">
        <div className="flex items-center gap-8 pr-8">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="font-display text-4xl md:text-6xl text-stroke whitespace-nowrap">
              Let's Build Something<span className="text-primary text-stroke-primary"> · </span>
            </span>
          ))}
        </div>
      </Marquee>

      <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-16 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Link to="/" className="font-display text-3xl">JULIUS<span className="text-primary">.</span>MASON</Link>
          <p className="mt-5 text-muted-foreground max-w-sm leading-relaxed">
            Professional websites for businesses that want more customers. Custom web design, development, SEO,
            and website care — based in France, serving businesses worldwide.
          </p>
          <div className="mt-8">
            <p className="overline text-muted-foreground mb-3">Join the newsletter</p>
            <Newsletter source="footer" className="max-w-sm" />
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            {activeNetworks.map(({ key, Icon, url, label }) => (
              <a
                key={key}
                href={url}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={label}
                data-testid={`footer-social-${key}`}
                className="p-2.5 border border-border hover:border-primary hover:text-primary transition-colors"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <div className="lg:col-span-4 grid grid-cols-2 sm:grid-cols-3 gap-8 lg:col-start-6">
          {COLS.map((col) => (
            <div key={col.title}>
              <p className="overline text-muted-foreground mb-4">{col.title}</p>
              <ul className="space-y-2.5">
                {col.links.map(([label, to]) => (
                  <li key={to + label}>
                    <Link to={to} className="text-sm text-foreground/80 hover:text-primary transition-colors">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="lg:col-span-3">
          <p className="overline text-muted-foreground mb-4">Ready to grow?</p>
          <Link to="/start-project" data-testid="footer-cta" className="group inline-flex items-center gap-2 font-display text-2xl hover:text-primary transition-colors">
            Start My Project <ArrowUpRight className="w-6 h-6 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </Link>
          <a href="mailto:julius.a.mason@gmail.com" className="mt-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
            <Mail className="w-4 h-4" /> julius.a.mason@gmail.com
          </a>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 font-mono text-xs text-muted-foreground">
          <span>© {new Date().getFullYear()} Julius Mason. All rights reserved.</span>
          <span>Based in France · Serving businesses worldwide</span>
        </div>
      </div>
    </footer>
  );
}
